import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Skeleton, Typography } from "@mui/material";
import { ArrowForward, WarningAmber } from "@mui/icons-material";
import { toast } from "react-toastify";
import { PoComponent } from "../../../../models/general/po-component";
import { URLS } from "../../../../services/app-urls";
import { BaseService } from "../../../../services/base-service";
import { translate } from "../../../../translate/translate";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";

interface PoStatusSummaryItem {
  key: string;
  count: number;
  percentage: number;
}

interface PoStatusSummary {
  total: number;
  statuses: PoStatusSummaryItem[];
}

interface PoStatusStyle {
  color: string;
  highlight: boolean;
}

const PO_STATUS_STYLES: Record<string, PoStatusStyle> = {
  expired_date: { color: "#ef4444", highlight: true },
  waiting_invoice: { color: "#f59e0b", highlight: false },
  material_in_transit: { color: "#3b82f6", highlight: false },
};

const DEFAULT_STATUS_STYLE: PoStatusStyle = { color: "#9ca3af", highlight: false };

const formatPercentage = (percentage: number): string => {
  const language = translate.language;
  const value = Number.isFinite(percentage) ? percentage : 0;
  return `${value.toLocaleString(language === "en" ? "en-US" : "pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const PoStatusCardComponent = () => {
  const service = useMemo(() => new BaseService<PoComponent>(URLS.PO_COMPONENT), []);
  const navigate = useNavigate();
  const [summary, setSummary] = useState<PoStatusSummary>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    service.clearParameters();
    service.getFromListRoute("status-summary")
      .then(response => setSummary(response.data as unknown as PoStatusSummary))
      .catch(error => toast.error(translate.t("unknown_error"), error))
      .finally(() => setIsLoading(false));
  }, [service]);

  // Enquanto a resposta não chega, mantém a estrutura do card zerada para o layout não saltar
  const statuses = summary?.statuses ?? Object.keys(PO_STATUS_STYLES).map(key => ({ key, count: 0, percentage: 0 }));
  const totalLabel = summary ? parseLocaleNumber(summary.total) : "-";

  const styleOf = (key: string): PoStatusStyle => PO_STATUS_STYLES[key] ?? DEFAULT_STATUS_STYLE;

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        padding: "16px",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", marginBottom: "16px" }}>
        {/* width auto sobrescreve o `.MuiBox-root { width: 100% }` global, que esticaria os dois lados do header */}
        <Box sx={{ width: "auto", flex: "1 1 auto", minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Inter", fontSize: "14px", fontWeight: 600, color: "#374151" }}>
            {translate.t("purchase_orders_by_status")}
          </Typography>
          <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
            {translate.t("orders_total_count", { total: totalLabel })}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => navigate("/general/po")}
          sx={{
            display: "flex",
            alignItems: "center",
            alignSelf: "flex-start",
            gap: "2px",
            width: "auto",
            flex: "0 0 auto",
            marginLeft: "auto",
            border: "none",
            background: "none",
            padding: 0,
            cursor: "pointer",
            fontFamily: "Inter",
            fontSize: "12px",
            color: "#1e3f7a",
            whiteSpace: "nowrap",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate.t("view_all")}
          <ArrowForward sx={{ fontSize: "11px" }} />
        </Box>
      </Box>

      <Box
        aria-label={translate.t("purchase_orders_by_status")}
        sx={{
          display: "flex",
          height: "12px",
          gap: "1px",
          borderRadius: "9999px",
          overflow: "hidden",
          backgroundColor: "#f3f4f6",
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          statuses.map(status => (
            <Box
              key={status.key}
              sx={{ width: `${status.percentage}%`, height: "100%", backgroundColor: styleOf(status.key).color }}
            />
          ))
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginTop: "16px" }}>
        {statuses.map(status => {
          const { color, highlight } = styleOf(status.key);
          return (
            <Box
              key={status.key}
              sx={{ border: "1px solid #f3f4f6", borderRadius: "8px", padding: "10px", minWidth: 0 }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: color, flexShrink: 0, marginTop: "2px" }} />
                <Typography sx={{ fontFamily: "Inter", fontSize: "11px", fontWeight: 500, color: "#4b5563", lineHeight: 1.2 }}>
                  {translate.t(status.key)}
                </Typography>
                {highlight && <WarningAmber sx={{ fontSize: "12px", color: "#ef4444", flexShrink: 0 }} />}
              </Box>
              {isLoading ? (
                <Skeleton variant="text" width={60} height={24} sx={{ marginTop: "8px" }} />
              ) : (
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: 1,
                    marginTop: "8px",
                    color: highlight ? "#ef4444" : "#1f2937",
                  }}
                >
                  {parseLocaleNumber(status.count)}{" "}
                  <Typography component="span" sx={{ fontFamily: "Inter", fontSize: "12px", fontWeight: 500, color: "#6b7280" }}>
                    {translate.t("orders")}
                  </Typography>
                </Typography>
              )}
              {isLoading ? (
                <Skeleton variant="text" width={40} height={16} sx={{ marginTop: "4px" }} />
              ) : (
                <Typography sx={{ fontFamily: "Inter", fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>
                  {`${formatPercentage(status.percentage)} ${translate.t("of_total")}`}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PoStatusCardComponent;

